import "./ProjectCard.css";

import { Link, useLocation } from "react-router-dom";

import {
  CarbonReductionBlueIcon,
  CheckBlueIcon,
  DropdownLightIcon,
  LandholdingBlueIcon,
  MapPinGreyIcon,
  PhaseBlueIcon,
  PrizeBlueIcon,
  ScaleBlueIcon,
} from "../../../../../assets";
import { ProjectStatusConstants, ProjectTypeConstants } from "../../../../../constants";
import { IStateOrigin } from "../../../../../navigators/navigateStateObjects";
import { SearchProjectsResponse } from "../../../../../service/publicquery";
import { useAuth } from "../../../../../useAuth";
import { formatNumber, getCountryNameByCode, getIconFromProjectType, getPlaceholderImage } from "../../../../../utils";
import { getLandholdingDashboardRoute, getProjectDetailsByUuid } from "../../../../../utils/routes";
import { Button, Column, Row } from "../../../../../widget";
import { useProjectCard } from "./useProjectCard";

export interface InvestmentProjectCardProps {
  isLandholdingProject: boolean;
  imageSource: string | null;
  uuid: string;
  name: string;
  addressCountryCode: string;
  addressRegion: string | null;
  developerSmallLogoFileUrl: string | null;
  developerDisplayName?: string;
  standardDisplayName?: string;
  cachedAvailableQuantity: number | null;
  avgPrice: number | null;
  status: string;
  phase: string;
  projectType: string;
  projectTypeUuid: string;
  areaNetHa: number | null;
  tco2e: number | null;
  className?: string;
  landholding: SearchProjectsResponse["results"][0]["landholdings"][0] | null;
}

// Units available for sale with no average issuance price are priced on application.
const getFormattedAvgPrice = (avgPrice: number | null, cachedAvailableQuantity: number | null): string => {
  if (avgPrice !== null) return `£${formatNumber(avgPrice, 2)}`;
  return (cachedAvailableQuantity ?? 0) > 0 ? "POA" : "-";
};

export const ProjectCard = ({
  isLandholdingProject,
  imageSource,
  uuid,
  name,
  addressCountryCode,
  addressRegion,
  developerSmallLogoFileUrl,
  developerDisplayName,
  standardDisplayName,
  cachedAvailableQuantity,
  avgPrice,
  status,
  phase,
  projectType,
  projectTypeUuid,
  areaNetHa,
  tco2e,
  className,
  landholding,
}: InvestmentProjectCardProps): JSX.Element => {
  const stateLocation = useLocation();
  const { currentUserType } = useAuth();
  const { onProjectCardClick, onLandholdingCardClick, onNewDealClick, landholdingProjects, showLandholdingProjects } =
    useProjectCard({
      projectUuid: uuid,
      landholdingUuid: landholding?.groupUuid,
    });

  const uniqueProjectTypes = landholding
    ? Array.from(new Map(landholding.projectTypes.map((pt) => [pt.uuid, pt])).values())
    : [];
  const uniqueDevelopers = landholding
    ? Array.from(new Map(landholding.developers.map((developer) => [developer.uuid, developer])).values())
    : [];
  const detailsRoute = landholding
    ? getLandholdingDashboardRoute(landholding.groupUuid, currentUserType)
    : getProjectDetailsByUuid(uuid, currentUserType);
  const detailsState = {
    origin: {
      goBackText: "Back to investment directory",
      from: stateLocation.pathname,
    } as IStateOrigin,
  };

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        className={`InvestmentProjectCard ${className || ""} ${isLandholdingProject ? "LandholdingProject" : ""}`}
        onClick={landholding ? onLandholdingCardClick : onProjectCardClick}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            if (landholding) onLandholdingCardClick();
            if (!landholding) onProjectCardClick();
          }
        }}
      >
        <div className="InvestmentProjectCardLeft">
          <Link
            to={detailsRoute}
            state={detailsState}
            className="InvestmentProjectCardImageLink"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="InvestmentProjectCardImage"
              style={{
                backgroundImage: imageSource
                  ? `url(${imageSource})`
                  : `url(${getPlaceholderImage(standardDisplayName)})`,
              }}
            />
          </Link>

          <div className="InvestmentProjectCardContent">
            <div className="InvestmentProjectCardHeader">
              <div className="InvestmentProjectCardHeaderRow">
                {landholding && <LandholdingBlueIcon />}
                <h3>
                  {landholding ? (
                    <Link
                      to={detailsRoute}
                      state={detailsState}
                      className="InvestmentProjectCardNameLink"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {landholding.groupName}
                    </Link>
                  ) : (
                    <Link
                      to={detailsRoute}
                      state={detailsState}
                      className="InvestmentProjectCardNameLink"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {name}
                    </Link>
                  )}
                </h3>
                {landholding && (
                  <DropdownLightIcon
                    className={showLandholdingProjects ? "FlippedIcon" : ""}
                    rotate={showLandholdingProjects ? 180 : undefined}
                  />
                )}
              </div>
              <div className="InvestmentProjectCardPillContainer">
                {landholding ? (
                  uniqueProjectTypes.map((pt) => (
                    <div
                      key={pt.uuid}
                      className="InvestmentProjectCardPill"
                      style={{
                        color: ProjectTypeConstants.PROJECT_TYPE_PILL_COLOR_MAP[pt.displayName]?.[0],
                        backgroundColor: ProjectTypeConstants.PROJECT_TYPE_PILL_COLOR_MAP[pt.displayName]?.[1],
                      }}
                    >
                      {getIconFromProjectType(pt.uuid, 14)}
                      <span className="body3">{pt.displayName}</span>
                    </div>
                  ))
                ) : (
                  <div
                    className="InvestmentProjectCardPill"
                    style={{
                      color: ProjectTypeConstants.PROJECT_TYPE_PILL_COLOR_MAP[projectType]?.[0],
                      backgroundColor: ProjectTypeConstants.PROJECT_TYPE_PILL_COLOR_MAP[projectType]?.[1],
                    }}
                  >
                    {getIconFromProjectType(projectTypeUuid, 14)}
                    <span className="body3">{projectType}</span>
                  </div>
                )}
                {addressCountryCode && (
                  <div className="InvestmentProjectCardLocation">
                    <MapPinGreyIcon />
                    <span className="body3">{`${addressRegion ? `${addressRegion}, ` : ""}${getCountryNameByCode(addressCountryCode)}, UK`}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="InvestmentProjectCardPillContainer">
              {landholding ? (
                uniqueDevelopers.map((d) => (
                  <div key={d.uuid} className="InvestmentProjectCardPill">
                    {d.files && d.files[0]?.file.url && <img src={d.files[0]?.file.url} alt="DeveloperLogo" />}
                    <span className="body3">{d.displayName}</span>
                  </div>
                ))
              ) : (
                <div className="InvestmentProjectCardPill">
                  {developerSmallLogoFileUrl && <img src={developerSmallLogoFileUrl} alt="DeveloperLogo" />}
                  <span className="body3">{developerDisplayName}</span>
                </div>
              )}
              {!landholding && standardDisplayName && (
                <div className="InvestmentProjectCardPill">
                  <PrizeBlueIcon /> <span className="body3">{standardDisplayName}</span>
                </div>
              )}
              {(areaNetHa || landholding?.calculatedTotalAreaNetHa) && (
                <div className="InvestmentProjectCardPill">
                  <ScaleBlueIcon />
                  <span className="body3">
                    {formatNumber(landholding?.calculatedTotalAreaNetHa ?? areaNetHa ?? undefined)} hectares
                  </span>
                </div>
              )}
              {!landholding && tco2e && (
                <div className="InvestmentProjectCardPill">
                  <CarbonReductionBlueIcon />
                  <span className="body3">{formatNumber(tco2e)} tCO2e</span>
                </div>
              )}
              {!landholding &&
                (status === ProjectStatusConstants.VALIDATED ||
                  status === ProjectStatusConstants.VERIFICATION_PENDING) && (
                  <div className="InvestmentProjectCardPill">
                    <CheckBlueIcon />
                    <span className="body3">Validated</span>
                  </div>
                )}
              {!landholding &&
                (status === ProjectStatusConstants.VERIFIED ||
                  status === ProjectStatusConstants.ONGOING_VERIFICATION) && (
                  <div className="InvestmentProjectCardPill">
                    <CheckBlueIcon />
                    <span className="body3">Verified</span>
                  </div>
                )}
              {!landholding && phase && (
                <div className="InvestmentProjectCardPill">
                  <PhaseBlueIcon />
                  <span className="body3">Phase: {phase}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="InvestmentProjectCardDivider" />
        <div className="InvestmentProjectCardCenter">
          {landholding ? (
            <div className="InvestmentProjectCardMetric">
              <p className="body2">Available units</p>
              <p className="body1 InvestmentProjectCardMetricValue">
                {formatNumber(landholding.calculatedTotalAvailableQuantity)}
              </p>
            </div>
          ) : (
            <div className="InvestmentProjectCardMetric">
              <p className="body2">Available units</p>
              <p className="body1 InvestmentProjectCardMetricValue">
                {cachedAvailableQuantity !== null ? formatNumber(cachedAvailableQuantity) : "-"}
              </p>
            </div>
          )}
          <div className="InvestmentProjectCardStatDivider" />
          {landholding ? (
            <div className="InvestmentProjectCardMetric">
              <p className="body2">Projects</p>
              <p className="body1 InvestmentProjectCardMetricValue">
                {formatNumber(landholding.calculatedTotalProjects)}
              </p>
            </div>
          ) : (
            <div className="InvestmentProjectCardMetric">
              <p className="body2">Avg price</p>
              <p className="body1 InvestmentProjectCardMetricValue">
                {getFormattedAvgPrice(avgPrice, cachedAvailableQuantity)}
              </p>
            </div>
          )}
        </div>
        <div className="InvestmentProjectCardDivider" />
        <div className="InvestmentProjectCardRight">
          <Button
            text="New deal"
            corners="rounded"
            variant="tertiary"
            size="small"
            hasNoPadding
            onClick={(e) => {
              e.stopPropagation();
              if (landholding) onNewDealClick(landholding.groupUuid, "Landholding", landholding.groupName);
              if (!landholding) onNewDealClick(uuid, "Project", name);
            }}
          />
        </div>
      </div>
      {showLandholdingProjects && landholdingProjects && (
        <div className="LandholdingProjects">
          {landholdingProjects?.map((p, index) => (
            <Row key={p.uuid} spacingV={index < landholdingProjects.length - 1 ? "ml" : undefined}>
              <Column span={12}>
                <ProjectCard
                  isLandholdingProject
                  className="InvestmentDirectoryProjectCard"
                  imageSource={p.imageSource}
                  uuid={p.uuid}
                  name={p.name}
                  addressCountryCode={p.addressCountryCode}
                  addressRegion={p.addressRegion}
                  developerSmallLogoFileUrl={p.developerSmallLogoFileUrl}
                  developerDisplayName={p.developerDisplayName}
                  standardDisplayName={p.standardDisplayName}
                  cachedAvailableQuantity={p.cachedAvailableQuantity}
                  status={p.status}
                  phase={p.phase}
                  projectType={p.projectType}
                  projectTypeUuid={p.projectTypeUuid}
                  areaNetHa={p.areaNetHa}
                  avgPrice={p.avgPrice}
                  tco2e={p.tco2e}
                  landholding={null}
                />
              </Column>
            </Row>
          ))}
        </div>
      )}
    </>
  );
};
